import {
  ApdmCampaignPlaceholder,
  ApdmCampaignStatus,
  ApdmCampaignVersionRef,
  ApdmCampaignView,
} from './campaign';
import { ApdmAudienceCandidate } from './campaign-api';
import { ApdmCouponCampaignSummary } from './campaign-coupon';

export interface RpaCardMessageBase {
  appId: string;
  userName: string;
  pcOpenAppId: string;
}
export type RpaOutboundMessage =
  | { kind: 'text'; text: string }
  | (RpaCardMessageBase & {
      kind: 'miniprogram';
      path: string;
      title: string;
      thumbUrl?: string;
    })
  | (RpaCardMessageBase & {
      kind: 'link';
      url: string;
      title: string;
      desc?: string;
      thumbUrl?: string;
    })
  | { kind: 'image'; url: string }
  | { kind: 'video'; url: string };
export interface RpaPrivateChatSendCapacity {
  liveAccounts: number;
  throttledAccounts: number;
  earliestAvailableAt: string | null;
}
export enum ApdmContactBlockReason {
  CampaignInactive = 'campaign_inactive',
  OutsideWindow = 'outside_window',
  DoNotContact = 'do_not_contact',
  Cooldown = 'cooldown',
  OpenOrder = 'open_order',
  WorkingLead = 'working_lead',
  UnansweredProactive = 'unanswered_proactive',
  AlreadyReached = 'already_reached',
  RecipientNotDue = 'recipient_not_due',
  CustomerReplied = 'customer_replied',
  StaffPaused = 'staff_paused',
}
export enum ApdmCampaignPreviewWarning {
  StartRequired = 'start_required',
  WindowExpired = 'window_expired',
  NoLiveAccounts = 'no_live_accounts',
  AccountsThrottled = 'accounts_throttled',
  SustainableRateUnknown = 'sustainable_rate_unknown',
  InsufficientWindow = 'insufficient_window',
}
export interface ApdmCampaignPreviewCounts {
  total: number;
  known: number;
  unconfirmed: number;
  excluded: number;
  exclusionReasons: Array<{ reason: ApdmContactBlockReason; count: number }>;
}
export type ApdmCampaignPreviewSample = {
  candidate: ApdmAudienceCandidate;
} & (
  | { kind: 'ready'; variantId: string; messages: RpaOutboundMessage[] }
  | { kind: 'unavailable' }
);
export interface ApdmCampaignPreviewResult {
  campaign: ApdmCampaignView;
  asOf: string;
  counts: ApdmCampaignPreviewCounts;
  samples: ApdmCampaignPreviewSample[];
  variantExamples?: ApdmVariantReviewExample[];
  capacity: RpaPrivateChatSendCapacity;
  scheduledCapacityUpperBound: number | null;
  warnings: ApdmCampaignPreviewWarning[];
  /** The coupon campaign the messages carry; null when none is chosen. */
  coupon: ApdmCouponCampaignSummary | null;
}
export type ApdmCampaignPreviewRequest = ApdmCampaignVersionRef;

/** A chat reply's preview card, named by the reply that shows it. */
export interface ApdmCampaignPreviewSnapshotRequest
  extends ApdmCampaignVersionRef {
  messageId: string;
}

/**
 * A preview exactly as a chat reply showed it, read back rather than counted
 * again, beside the campaign as it is now. `expired` is a reply with no kept
 * preview whose draft has since moved on, so it can no longer be counted.
 */
export type ApdmCampaignPreviewSnapshot = {
  /** Whether the draft moved on or started since this preview. */
  current: { version: number; status: ApdmCampaignStatus };
} & (
  | { kind: 'preview'; preview: ApdmCampaignPreviewResult }
  | { kind: 'expired' }
);

export type ApdmVariantReviewExample = { variantIndex: number } & (
  | {
      kind: 'ready';
      candidate: ApdmAudienceCandidate;
      text: string;
      applicabilityMatches: boolean;
    }
  | { kind: 'unavailable'; missingFields: ApdmCampaignPlaceholder[] }
  | { kind: 'no_sample' }
);
